/**
 * Geographic region definitions and utility functions.
 *
 * @module components/ui/geoRegions
 */

/**
 * Geographic region definitions with their associated Azure regions
 */
export const GEO_REGIONS = {
  americas: {
    name: 'Americas',
    description: 'United States, Canada, Mexico',
    regions: [
      'canadacentral',
      'centralus',
      'eastus2',
      'mexicocentral',
      'northcentralus',
      'westcentralus',
      'westus2',
    ],
  },
  europe: {
    name: 'Europe',
    description: 'UK, Northern Europe, Sweden',
    regions: ['northeurope', 'swedencentral', 'uksouth'],
  },
  apac: {
    name: 'Asia Pacific',
    description: 'East Asia, Australia',
    regions: ['eastasia', 'australiaeast'],
  },
} as const;

export type GeoRegionKey = keyof typeof GEO_REGIONS;

/**
 * Get which geo regions are selected based on allowed locations
 */
export function getSelectedGeoRegions(allowedLocations: string[]): Set<GeoRegionKey> {
  const selected = new Set<GeoRegionKey>();
  const locationsSet = new Set(allowedLocations.map((l) => l.toLowerCase()));

  for (const [key, geo] of Object.entries(GEO_REGIONS)) {
    // A geo region is selected if any of its regions are in allowedLocations
    if (geo.regions.some((r) => locationsSet.has(r))) {
      selected.add(key as GeoRegionKey);
    }
  }

  return selected;
}

/**
 * Get all Azure regions from selected geo regions
 */
export function getRegionsFromGeoSelection(selectedGeos: Set<GeoRegionKey>): string[] {
  const regions: string[] = [];
  for (const geoKey of selectedGeos) {
    regions.push(...GEO_REGIONS[geoKey].regions);
  }
  return regions;
}
